import { toast } from 'react-toastify';
import '@/styles/createUserStyles.scss';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaBuilding, FaSitemap, FaCog, FaTimes, FaUserPlus, FaExclamationTriangle } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * CreateUser Component
 * 
 * Creates a new user with all configuration options including:
 * - Basic info (name, email, phone)
 * - Role assignment
 * - Department assignment
 * - Manager assignment
 * - Director status
 * - Active status
 */
const CreateUser = ({ onClose }) => {
    // Basic user info
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [phonenumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    
    // User configuration
    const [roleID, setRoleID] = useState('');
    const [departmentID, setDepartmentID] = useState('');
    const [managerUserID, setManagerUserID] = useState('');
    const [isDirector, setIsDirector] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [accountExpiryDate, setAccountExpiryDate] = useState('');
    
    // Data for dropdowns
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [managers, setManagers] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [similarUsers, setSimilarUsers] = useState([]);
    const { apiFetch, logout } = useAuth();
    const [sessionExpired, setSessionExpired] = useState(false);
    const navigate = useNavigate();
    const nameDebounceRef = useRef(null);

    const checkSimilarName = useCallback(async (fn, ln) => {
        const first = fn?.trim() || '';
        const last = ln?.trim() || '';
        if (first.length < 2 && last.length < 2) { setSimilarUsers([]); return; }
        try {
            const res = await apiFetch(`/api/User/check-similar-name?firstName=${encodeURIComponent(first)}&lastName=${encodeURIComponent(last)}`);
            const data = await res.json();
            if (data.success) setSimilarUsers(data.data ?? []);
        } catch { /* silent */ }
    }, [apiFetch]);

    const handleFirstNameChange = (e) => {
        setFirstname(e.target.value);
        clearTimeout(nameDebounceRef.current);
        nameDebounceRef.current = setTimeout(() => checkSimilarName(e.target.value, lastname), 500);
    };

    const handleLastNameChange = (e) => {
        setLastname(e.target.value);
        clearTimeout(nameDebounceRef.current);
        nameDebounceRef.current = setTimeout(() => checkSimilarName(firstname, e.target.value), 500);
    };

    // Fetch roles for dropdown
    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/roles');
            const data = await response.json();
            if (data.success && data.data) {
                setRoles(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching roles:', error);
        }
    }, [apiFetch]);

    // Fetch departments for dropdown
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success && data.data) {
                setDepartments(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching departments:', error);
        }
    }, [apiFetch]);

    // Fetch potential managers (all active users)
    const fetchManagers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/all');
            const data = await response.json();
            if (data.success && data.data) {
                setManagers([...data.data].sort((a, b) =>
                    (`${a.firstName || ''} ${a.lastName || ''}`).localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                ));
            } else if (Array.isArray(data.data)) {
                setManagers(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching managers:', error);
        }
    }, [apiFetch]);

    // Load all dropdown data on mount
    useEffect(() => {
        const loadData = async () => {
            setLoadingData(true);
            await Promise.all([fetchRoles(), fetchDepartments(), fetchManagers()]);
            setLoadingData(false);
        };
        loadData();
    }, [fetchRoles, fetchDepartments, fetchManagers]);

    // When isDirector changes, clear manager if director is selected
    useEffect(() => {
        if (isDirector) {
            setManagerUserID('');
        }
    }, [isDirector]);

    const handleSubmit = async () => {
        if (!firstname || !lastname || !email) {
            toast.error("Fields with * are required");
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        try {
            setIsLoading(true);

            const userData = {
                firstName: firstname,
                lastName: lastname,
                phoneNumber: phonenumber || null,
                email: email,
                isActive: isActive,
                roleID: roleID ? parseInt(roleID) : null,
                departmentID: departmentID ? parseInt(departmentID) : null,
                managerUserID: managerUserID ? parseInt(managerUserID) : null,
                isDirector: isDirector,
                accountExpiryDate: accountExpiryDate ? new Date(accountExpiryDate).toISOString() : null
            };

            const response = await apiFetch('/api/User/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            });

            const data = await response.json();

            if (data.success) {
                toast.success("User created successfully! A welcome email has been sent.");
                // Clear all fields
                setFirstname('');
                setLastname('');
                setPhoneNumber('');
                setEmail('');
                setRoleID('');
                setDepartmentID('');
                setManagerUserID('');
                setIsDirector(false);
                setIsActive(true);
                setAccountExpiryDate('');
                if (onClose) {
                    setTimeout(() => {
                        onClose();
                    }, 1200);
                }
            } else {
                toast.error(data.message || "Could not create user");
            }

            
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.log(e);
            toast.error("An error has occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (onClose) onClose();
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className="create-user">
            <div className="create-user-header">
                <div className="header-content">
                    <h2><FaUserPlus className="header-icon" /> Create User</h2>
                </div>
                {onClose && (
                    <button className="close-button" onClick={handleCancel}>
                        <FaTimes />
                    </button>
                )}
            </div>
            
            <div className="create-user-content">
                <div className="create-user-fields">
                    {/* Personal Information Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FaUser className="section-icon" />
                            <span>Personal Information</span>
                        </div>
                        
                        <div id="create-user-fullname">
                            <div className="create-user-names">
                                <label htmlFor="firstname">First Name <span className="required">*</span></label>
                                <input 
                                    type="text" 
                                    placeholder="Enter first name" 
                                    id="firstname" 
                                    value={firstname} 
                                    onChange={handleFirstNameChange}
                                    onBlur={() => checkSimilarName(firstname, lastname)}
                                />                        
                            </div>
                            <div className="create-user-names">
                                <label htmlFor="lastname">Last Name <span className="required">*</span></label>
                                <input 
                                    type="text" 
                                    placeholder="Enter last name" 
                                    id="lastname" 
                                    value={lastname} 
                                    onChange={handleLastNameChange}
                                    onBlur={() => checkSimilarName(firstname, lastname)}
                                />                 
                            </div>
                        </div>
                        {similarUsers.length > 0 && (
                            <div style={{
                                marginTop: 8,
                                padding: '8px 12px',
                                borderRadius: 7,
                                background: '#fffbeb',
                                border: '1px solid #fde68a',
                                fontSize: '0.82rem',
                                color: '#92400e'
                            }}>
                                <FaExclamationTriangle style={{ marginRight: 6 }} />
                                A user with a very similar name already exists — please confirm this is a different person:
                                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                    {similarUsers.map(u => (
                                        <li key={u.userID}>
                                            <strong>{u.firstName} {u.lastName}</strong>
                                            <span style={{ color: '#64748b', marginLeft: 8 }}>{u.userEmail}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        
                        <div className="create-user-email">
                            <label htmlFor="email">Email <span className="required">*</span></label>
                            <input 
                                type="email" 
                                placeholder="Enter email address" 
                                id="email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                            />                     
                        </div>
                        
                        <div className="create-user-phone">
                            <label htmlFor="phonenumber">Phone Number</label>
                            <input 
                                type="text" 
                                placeholder="Enter phone number" 
                                id="phonenumber" 
                                value={phonenumber} 
                                onChange={(e) => setPhoneNumber(e.target.value)} 
                            />                    
                        </div>
                    </div>

                    {/* Organization Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FaBuilding className="section-icon" />
                            <span>Organization</span>
                        </div>

                        <div className="create-user-field">
                            <label htmlFor="roleID">Role</label>
                            <select
                                id="roleID"
                                value={roleID}
                                onChange={(e) => setRoleID(e.target.value)}
                                disabled={loadingData}
                            >
                                <option value="">-- Select Role --</option>
                                {roles.map(role => (
                                    <option key={role.roleID} value={role.roleID}>
                                        {role.roleName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="create-user-field">
                            <label htmlFor="departmentID">Department</label>
                            <select
                                id="departmentID"
                                value={departmentID}
                                onChange={(e) => setDepartmentID(e.target.value)}
                                disabled={loadingData}
                            >
                                <option value="">-- Select Department --</option>
                                {departments.map(dept => (
                                    <option key={dept.departmentID} value={dept.departmentID}>
                                        {dept.departmentName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Hierarchy Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FaSitemap className="section-icon" />
                            <span>Hierarchy</span>
                        </div>

                        <div className="checkbox-card-row">
                            <label className="checkbox-card">
                                <input
                                    type="checkbox"
                                    checked={isDirector}
                                    onChange={(e) => setIsDirector(e.target.checked)}
                                />
                                <div className="checkbox-content">
                                    <span className="checkbox-title">Director</span>
                                    <span className="checkbox-desc">Top-level, no manager above</span>
                                </div>
                            </label>
                        </div>

                        {!isDirector && (
                            <div className="create-user-field">
                                <label htmlFor="managerUserID">Direct Manager</label>
                                <select
                                    id="managerUserID"
                                    value={managerUserID}
                                    onChange={(e) => setManagerUserID(e.target.value)}
                                    disabled={loadingData || isDirector}
                                >
                                    <option value="">-- Select Manager --</option>
                                    {managers.map(mgr => {
                                        const mgrId = mgr.userID || mgr.id;
                                        const mgrName = mgr.firstName && mgr.lastName 
                                            ? `${mgr.firstName} ${mgr.lastName}` 
                                            : mgr.name || mgr.email || `User ${mgrId}`;
                                        return (
                                            <option key={mgrId} value={mgrId}>
                                                {mgrName}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Status Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FaCog className="section-icon" />
                            <span>Status</span>
                        </div>

                        <div className="checkbox-card-row">
                            <label className="checkbox-card active-card">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <div className="checkbox-content">
                                    <span className="checkbox-title">Active User</span>
                                    <span className="checkbox-desc">Inactive users cannot log in</span>
                                </div>
                            </label>
                        </div>

                        <div className="create-user-field">
                            <label htmlFor="accountExpiryDate">
                                Account Expiry Date & Time (Optional)
                            </label>
                            <input
                                type="datetime-local"
                                id="accountExpiryDate"
                                value={accountExpiryDate}
                                onChange={(e) => setAccountExpiryDate(e.target.value)}
                                placeholder="Set expiration date and time"
                            />
                            <small className="field-hint">
                                Set an expiry date for users who have submitted resignation notice. 
                                The user will be automatically prevented from logging in after this date/time.
                            </small>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="create-user-buttons">
                        <button
                            className="button-secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className="button-primary"
                            disabled={isLoading || loadingData}
                            onClick={handleSubmit}
                        >
                            {isLoading ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </div>
            </div>
</div>
    );
};

export default CreateUser;